import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { CiViewTable } from "react-icons/ci";
import { FaRegChartBar } from "react-icons/fa6";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-moment";
import { format } from "date-fns";

import {
  getAllEmployeesActivityQueryOptions,
  getAllEmployeesForSAQueryOptions,
} from "@/config/queries/superadmin-query-options";
import { convertUTCtoIST } from "@/utils";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs";
import { DataTable } from "./comps/activity-table/data-table";
import { columns } from "./comps/activity-table/columns";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Label } from "@/components/ui/label/label";
import { Button } from "@/components/ui/button/button";
import { ScrollArea } from "@/components/ui/scroll-area/scroll-area";
import { useDateRangeStore } from "./comps/date-range-picker/use-date-range-store";
import { DateRangePicker } from "./comps/date-range-picker/date-range-picker2";

// Register the zoom plugin
ChartJS.register(zoomPlugin);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function getTotalSecondsFromDate(d: string): number {
  // Convert the input date string to a Date object
  const date = new Date(d);

  // Get hours, minutes, and seconds from the IST date
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // Calculate total seconds
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  return totalSeconds;
}

function calculateTimeDifference(
  dateString1: string,
  dateString2: string
): string {
  const date1 = new Date(dateString1);
  const date2 = new Date(dateString2);

  // Calculate the difference in milliseconds
  const timeDifference = Math.abs(date2.getTime() - date1.getTime());

  // Convert milliseconds to hours and minutes
  const hours = Math.floor(timeDifference / (1000 * 60 * 60));
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

  return `${hours} hours and ${minutes} minutes`;
}

function secondsToTimeString(seconds: number | null): string {
  if (seconds === null) {
    return "Not Found";
  }
  // Calculate hours, minutes, and determine AM/PM
  const hours = Math.floor(seconds / 3600) % 24;
  const minutes = Math.floor((seconds % 3600) / 60);
  const period = hours >= 12 ? "PM" : "AM";

  // Adjust hours for PM notation
  const adjustedHours = hours % 12 === 0 ? 12 : hours % 12;

  // Format the hours and minutes as HH:mm with AM/PM notation
  const formattedHours = String(adjustedHours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");

  // Return the formatted time string
  return `${formattedHours}:${formattedMinutes}${period}`;
}

const EmployeesActivityForSuperAdmin = () => {
  const user = Cookies.get("key") as string;

  const [whichTab, setWhichTab] = useState("table");

  const { data, isPending, isFetching } = useQuery(
    getAllEmployeesForSAQueryOptions(user)
  );

  const [selectedEmps, setSelectedEmps] = useState<string[]>([]);

  const employeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.employees === undefined) {
      return [];
    }

    const result = data?.employees?.map((emp) => {
      return {
        id: emp?._id,
        name: emp?.name,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  const { dateRange } = useDateRangeStore();

  const startdate = useMemo(() => {
    if (whichTab === "table") {
      return dateRange.from as Date;
    }

    return new Date();
  }, [dateRange, whichTab]);

  const enddate = useMemo(() => {
    if (whichTab === "table") {
      return dateRange.to as Date;
    }

    return new Date();
  }, [dateRange, whichTab]);

  const {
    data: empAllActivities,
    isPending: isAllActivitiesPending,
    isFetching: isAllActivitiesFetching,
  } = useQuery(
    getAllEmployeesActivityQueryOptions(
      user,
      convertUTCtoIST(new Date(startdate.setHours(0))),
      convertUTCtoIST(new Date(enddate.setHours(23)))
    )
  );

  console.log("empAllActivities", empAllActivities);

  const employees = useMemo(() => {
    if (isPending || isAllActivitiesPending) {
      return [];
    }

    if (
      empAllActivities === undefined ||
      empAllActivities?.activity === undefined
    ) {
      return [];
    }

    const logins = empAllActivities?.activity?.filter(
      (entry) => entry?.mode === "LOGIN"
    );

    const logouts = empAllActivities?.activity?.filter(
      (entry) => entry?.mode === "LOGOUT"
    );

    const pairedLogs: {
      inLog: {
        mode: string;
        createdAt: string;
        employee: {
          _id: string;
          name: string;
        };
      };
      outLog: {
        mode: string;
        createdAt: string;
        employee?: {
          _id: string;
          name: string;
        };
      } | null;
    }[] = [];

    for (const inLog of logins) {
      const correspondingOutLog = logouts?.find(
        (outlog) =>
          new Date(outlog?.createdAt).getDate() ===
            new Date(inLog?.createdAt).getDate() &&
          inLog?.employee?._id === outlog?.employee?._id &&
          !pairedLogs.some((pair) => pair.outLog === outlog)
      );

      if (correspondingOutLog) {
        pairedLogs.push({
          inLog,
          outLog: correspondingOutLog,
        });
      } else {
        pairedLogs.push({
          inLog,
          outLog: null,
        });
      }
    }

    let result: {
      id: string;
      name: string;
      loginTime: number;
      logoutTime: number | undefined;
      date: string;
      borderColor: string;
      y: number;
    }[] = [];

    let helper = 5;

    for (const pair of pairedLogs) {
      if (pair.outLog !== null) {
        const loginTime = getTotalSecondsFromDate(pair.inLog?.createdAt);
        const logoutTime = getTotalSecondsFromDate(pair.outLog?.createdAt);
        const date = format(new Date(pair.inLog?.createdAt), "PP");

        const already = result?.find(
          (res) => res.id === pair.inLog?.employee?._id
        );

        if (already) {
          result.push({
            loginTime,
            logoutTime,
            date,
            id: pair.inLog?.employee?._id,
            name: pair.inLog?.employee?.name,
            borderColor: "rgba(54, 162, 235, 1)",
            y: already?.y,
          });
        } else {
          result.push({
            loginTime,
            logoutTime,
            date,
            id: pair.inLog?.employee?._id,
            name: pair.inLog?.employee?.name,
            borderColor: "rgba(54, 162, 235, 1)",
            y: helper,
          });

          helper = helper + 5;
        }
      } else {
        const loginTime = getTotalSecondsFromDate(pair.inLog?.createdAt);
        const logoutTime = undefined;
        const date = format(new Date(pair.inLog?.createdAt), "PP");

        const already = result?.find(
          (res) => res.id === pair.inLog?.employee?._id
        );

        if (already) {
          result.push({
            loginTime,
            logoutTime,
            date,
            id: pair.inLog?.employee?._id,
            name: pair.inLog?.employee?.name,
            borderColor: "rgba(54, 162, 235, 1)",
            y: already?.y,
          });
        } else {
          result.push({
            loginTime,
            logoutTime,
            date,
            id: pair.inLog?.employee?._id,
            name: pair.inLog?.employee?.name,
            borderColor: "rgba(54, 162, 235, 1)",
            y: helper,
          });

          helper = helper + 5;
        }
      }
    }

    return result;
  }, [data, isAllActivitiesPending, isAllActivitiesFetching]);

  const dataForTable = useMemo(() => {
    if (isPending || isAllActivitiesPending) {
      return [];
    }

    if (
      empAllActivities === undefined ||
      empAllActivities?.activity === undefined
    ) {
      return [];
    }

    const logins = empAllActivities?.activity?.filter(
      (entry) => entry?.mode === "LOGIN"
    );

    const logouts = empAllActivities?.activity?.filter(
      (entry) => entry?.mode === "LOGOUT"
    );

    const pairedLogs: {
      inLog: {
        mode: string;
        createdAt: string;
        employee: {
          _id: string;
          name: string;
        };
        browser: string;
        device: string;
        ip: string;
        os: string;
        screenSize: string;
      };
      outLog: {
        mode: string;
        createdAt: string;
        employee?: {
          _id: string;
          name: string;
        };
        browser: string;
        device: string;
        ip: string;
        os: string;
        screenSize: string;
      } | null;
    }[] = [];

    for (const inLog of logins) {
      const correspondingOutLog = logouts?.find(
        (outlog) =>
          new Date(outlog?.createdAt).getDate() ===
            new Date(inLog?.createdAt).getDate() &&
          inLog?.employee?._id === outlog?.employee?._id &&
          !pairedLogs.some((pair) => pair.outLog === outlog)
      );

      if (correspondingOutLog) {
        pairedLogs.push({
          inLog,
          outLog: correspondingOutLog,
        });
      } else {
        pairedLogs.push({
          inLog,
          outLog: null,
        });
      }
    }

    let result: {
      id: string;
      name: string;
      loginTime: string;
      logoutTime: string | undefined;
      date: string;
      totalWorkedHours: string;
      loginInfo?: {
        ip: string;
        device: string;
        os: string;
        screenSize: string;
        browser: string;
      };
      logoutInfo?: {
        ip: string;
        device: string;
        os: string;
        screenSize: string;
        browser: string;
      };
    }[] = [];

    for (const pair of pairedLogs) {
      if (pair.outLog !== null) {
        const loginTime = pair.inLog?.createdAt;
        const logoutTime = pair.outLog?.createdAt;
        const date = format(new Date(pair.inLog?.createdAt), "PP");

        result.push({
          loginTime,
          logoutTime,
          date,
          id: pair.inLog?.employee?._id,
          name: pair.inLog?.employee?.name,
          totalWorkedHours: calculateTimeDifference(loginTime, logoutTime),
          loginInfo: {
            ip: pair.inLog?.ip,
            device: pair.inLog?.device,
            os: pair.inLog?.os,
            browser: pair.inLog?.browser,
            screenSize: pair.inLog?.screenSize,
          },
          logoutInfo: {
            ip: pair.outLog?.ip,
            device: pair.outLog?.device,
            os: pair.outLog?.os,
            browser: pair.outLog?.browser,
            screenSize: pair.outLog?.screenSize,
          },
        });
      } else {
        const loginTime = pair.inLog?.createdAt;
        const logoutTime = undefined;
        const date = format(new Date(pair.inLog?.createdAt), "PP");

        result.push({
          loginTime,
          logoutTime,
          date,
          id: pair.inLog?.employee?._id,
          name: pair.inLog?.employee?.name,
          totalWorkedHours: "Working",
          loginInfo: {
            ip: pair.inLog?.ip,
            device: pair.inLog?.device,
            os: pair.inLog?.os,
            browser: pair.inLog?.browser,
            screenSize: pair.inLog?.screenSize,
          },
          logoutInfo: undefined,
        });
      }
    }

    return result;
  }, [data, isAllActivitiesPending, isAllActivitiesFetching]);

  const labels2 = [
    // 8:00am
    28800,

    // 9:00am
    32400,

    // 10:00am
    36000,

    // 11:00am
    39600,

    // 12:00pm
    43200,

    // 1:00pm
    46800,

    // 2:00pm
    50400,

    // 3:00pm
    54000,

    // 4:00pm
    57600,

    // 5:00pm
    61200,

    // 6:00pm
    64800,

    // 7:00pm
    68400,

    // 8:00pm
    72000,
  ];

  // const employees = [
  //   {
  //     id: "A",
  //     name: "Employee A",
  //     login: getTotalSecondsFromDate("2024-04-29T09:00:00.000+00:00"),
  //     logout: getTotalSecondsFromDate("2024-04-29T12:00:00.000+00:00"),
  //     borderColor: "rgba(255, 99, 132, 1)",
  //     y: 5,
  //   },
  //   {
  //     id: "B",
  //     name: "Employee B",
  //     login: getTotalSecondsFromDate("2024-04-29T10:00:00.000+00:00"),
  //     logout: getTotalSecondsFromDate("2024-04-29T13:30:00.000+00:00"),
  //     borderColor: "rgba(54, 162, 235, 1)",
  //     y: 10,
  //   },
  //   {
  //     id: "A",
  //     name: "Employee A",
  //     login: getTotalSecondsFromDate("2024-04-29T12:33:00.000+00:00"),
  //     logout: getTotalSecondsFromDate("2024-04-29T18:00:00.000+00:00"),
  //     borderColor: "rgba(255, 99, 132, 1)",
  //     y: 5,
  //   },
  //   {
  //     id: "B",
  //     name: "Employee B",
  //     login: getTotalSecondsFromDate("2024-04-29T14:00:00.000+00:00"),
  //     logout: getTotalSecondsFromDate("2024-04-29T18:30:00.000+00:00"),
  //     borderColor: "rgba(54, 162, 235, 1)",
  //     y: 10,
  //   },
  // ];

  const completeChartData = useMemo(() => {
    let refinedLabels = labels2;

    employees.forEach((emp) => {
      if (!refinedLabels.includes(emp.loginTime)) {
        refinedLabels.push(emp.loginTime);
      }
      if (emp.logoutTime && !refinedLabels.includes(emp.logoutTime)) {
        refinedLabels.push(emp.logoutTime);
      }
    });

    refinedLabels = refinedLabels.sort((a, b) => a - b);

    let datasets: {
      id: string;
      label: string;
      borderColor: string;
      data: {
        x: number | undefined;
        y: number | undefined;
      }[];
    }[] = employeesData.map((emp) => {
      return {
        id: emp?.id,
        label: emp?.name,
        data: [],
        borderColor: "",
      };
    });

    employees.forEach((emp) => {
      const isAlreadyExist = datasets?.find((d) => d?.id === emp?.id);
      if (!isAlreadyExist) {
        datasets.push({
          id: emp.id,
          label: emp.name,
          borderColor: emp.borderColor,
          data: [
            {
              x: emp.loginTime,
              y: emp.y,
            },
            {
              x: emp.logoutTime,
              y: emp.y,
            },
          ],
        });
      } else {
        datasets = datasets.map((dataset) => {
          if (dataset.id === isAlreadyExist.id) {
            return {
              ...isAlreadyExist,
              data: [
                ...isAlreadyExist.data,
                { x: undefined, y: undefined },
                {
                  x: emp.loginTime,
                  y: emp.y,
                },
                {
                  x: emp.logoutTime,
                  y: emp.y,
                },
              ],
            };
          }

          return dataset;
        });
      }
    });

    const filteredEmpIndexes = selectedEmps?.map((id) => {
      const findIndex = employeesData?.findIndex((emp) => emp.id === id);

      if (findIndex !== -1) {
        return findIndex;
      }
    });

    if (filteredEmpIndexes.length > 0) {
      datasets = datasets.filter((_, index) => {
        if (filteredEmpIndexes?.includes(index)) {
          return true;
        }

        return false;
      });
    }

    return {
      labels: refinedLabels,
      datasets,
    };
  }, [employees, labels2, employeesData, isPending, isFetching, selectedEmps]);

  const yMapping: {
    [key: string]: string;
  } = useMemo(() => {
    const result: {
      [key: string]: string;
    } = {};

    for (const emp of employees) {
      const computed = String(emp?.y);
      result[computed] = emp.name;
    }

    return result;
  }, [employees]);

  return (
    <div className="p-3">
      <Tabs defaultValue="table" className="w-full">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger
              value="table"
              onClick={() => {
                setWhichTab("table");
              }}
            >
              <CiViewTable />
            </TabsTrigger>
            <TabsTrigger
              value="chart"
              onClick={() => {
                setWhichTab("chart");
              }}
            >
              <FaRegChartBar />
            </TabsTrigger>
          </TabsList>

          {whichTab === "table" && <DateRangePicker />}
        </div>
        <TabsContent value="table">
          <DataTable data={dataForTable} columns={columns} />
        </TabsContent>
        <TabsContent value="chart">
          <div className="h-[70vh]">
            <div className="flex items-center justify-end">
              <Popover>
                <PopoverTrigger>
                  <Button size="sm" variant="outline" className="mb-3">
                    {selectedEmps?.length > 0 ? (
                      <div>{selectedEmps?.length} Employee Selected</div>
                    ) : (
                      <div>Select Employees</div>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="mr-5">
                  <ScrollArea className="max-h-[200px] overflow-y-auto">
                    <div className="flex flex-col gap-3 max-h-[200px] overflow-y-auto">
                      {employeesData?.map((emp, index) => {
                        return (
                          <div key={index} className="flex items-center gap-1">
                            <Checkbox
                              id={`emp-${index}`}
                              checked={selectedEmps.includes(emp?.id)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? setSelectedEmps((prev) => [...prev, emp.id])
                                  : setSelectedEmps(
                                      selectedEmps?.filter(
                                        (empId) => empId !== emp.id
                                      )
                                    );
                              }}
                            />
                            <Label htmlFor={`emp-${index}`}>{emp?.name}</Label>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>
            <Line
              data={completeChartData}
              options={{
                scales: {
                  y: {
                    min: 0,
                    ticks: {
                      callback: (value) => {
                        const find = yMapping[String(value)];
                        return find;
                      },
                    },
                  },
                  x: {
                    ticks: {
                      callback: (value) => {
                        const find = completeChartData.labels[value as number];

                        return secondsToTimeString(find);
                      },
                    },
                  },
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  zoom: {
                    pan: {
                      enabled: true,
                      mode: "x",
                    },
                    zoom: {
                      wheel: {
                        enabled: true,
                      },
                      pinch: {
                        enabled: true,
                      },
                      mode: "x",
                    },
                  },
                  tooltip: {
                    callbacks: {
                      title: (tooltipItems) => {
                        const dataindex = tooltipItems[0].dataIndex;
                        const datasetIndex = tooltipItems[0].datasetIndex;
                        const dataset =
                          completeChartData.datasets[datasetIndex];
                        const find = dataset.data?.find(
                          (_, i) => i === dataindex
                        );

                        return secondsToTimeString(find!.x as number);
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeesActivityForSuperAdmin;
